import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Api from "../service/http";
import { hasActiveAccess as checkActiveAccess } from "../utils/subscription";

export const SUBSCRIPTION_FEATURES = [
  "14+ Hours on-demand content (Coming Soon!)",
  "85+ Hours of Live Interactive Sessions (Coming Soon!)",
  "100+ hours of Live Trading Sessions (Coming Soon!)",
  "Coaching",
  "App Chat Group",
];

/**
 * Shared Stripe / PayPal enrollment used by the public pricing section and
 * the in-app payment page.
 */
const useSubscriptionCheckout = ({
  successPath = "/profile",
  signinRedirect = "/signin?redirect=/&tag=pricing",
} = {}) => {
  const navigate = useNavigate();
  const [isLoggedIn] = useState(!!localStorage.getItem("accessToken"));
  const [user, setUser] = useState(
    JSON.parse(localStorage.getItem("user")) || null
  );

  const [program, setProgram] = useState({});
  const [programLoading, setProgramLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(1);
  const [selectedGateway, setSelectedGateway] = useState("stripe");
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [activating, setActivating] = useState(false);

  const hasActiveAccess = checkActiveAccess(user);

  const getProgram = async () => {
    try {
      const result = await Api.get("/web/program");
      if (result.status === 200) {
        setProgram(result.data);
      } else {
        console.error("Failed to fetch program data");
      }
    } catch (error) {
      console.error("Failed to fetch program data", error);
    } finally {
      setProgramLoading(false);
    }
  };

  const getProfile = async () => {
    const res = await Api.get("/web/profile");
    if (res?.data?.user) {
      localStorage.setItem("user", JSON.stringify(res?.data?.user));
      setUser(res?.data?.user);
    }
  };

  useEffect(() => {
    getProgram();
  }, []);

  useEffect(() => {
    if (Number(selectedPlan) === 2) {
      setSelectedGateway("paypal");
    }
  }, [selectedPlan]);

  useEffect(() => {
    if (isLoggedIn) {
      getProfile();
    }
  }, [isLoggedIn]);

  const enrollNow = async () => {
    if (!isLoggedIn) {
      window.location.href = signinRedirect;
      return;
    }
    setLoading(true);
    try {
      const result = await Api.post("/web/create-payment-intent", {
        term: selectedPlan,
        gateway: selectedGateway,
        program_id: program.id,
      });
      const data = result?.data ?? {};

      // The Api helper never rejects on 4xx/5xx, so failures have to be read
      // from the status. Without this the button did nothing at all when the
      // server refused (for example a 409 for an existing subscription).
      if (result?.status !== 200 || data.error) {
        const message =
          data.message ||
          (data.errors && Object.values(data.errors).flat().join(" ")) ||
          "We couldn't start the checkout. Please try again.";
        alert(message);
        if (result?.status === 409) await getProfile();
        return;
      }

      if (selectedGateway === "paypal") {
        if (!data.approveLink) {
          alert("PayPal did not return a checkout link. Please try again.");
          return;
        }
        window.location.href = data.approveLink;
        return;
      }

      if (!data.setupIntentClientSecret) {
        alert("We couldn't start the card payment. Please try again.");
        return;
      }

      setClientSecret(data.setupIntentClientSecret);
      setShowModal(true);
    } catch (error) {
      console.error("Error during enrollment:", error);
      alert("Something went wrong starting the checkout. Please try again.");
    }

    setLoading(false);
  };

  const resetCheckout = () => {
    setClientSecret(null);
    setShowModal(false);
    setActivating(false);
  };

  const finishActivation = async () => {
    await getProfile();
    navigate(successPath);
  };

  // Called by CardForm after confirmCardSetup succeeds. The card modal is
  // hidden here (not via onClose) so the "Activating…" state stays mounted.
  const onCardConfirmed = async (setupIntent) => {
    setShowModal(false);
    await handleStripeCompletion(setupIntent);
  };

  // Note: the Api helper never rejects on 4xx/5xx (validateStatus returns
  // the status), so every outcome has to be read from res.status / res.data.
  const handleStripeCompletion = async (setupIntent) => {
    if (setupIntent?.error) {
      alert("Error: " + setupIntent.error.message);
      return;
    }

    setActivating(true);
    try {
      const res = await Api.post("/web/create-subscription", {
        gateway: "stripe",
        program_id: program?.id,
        term: selectedPlan,
        payment_method_id: setupIntent.payment_method,
      });

      const data = res?.data ?? {};

      if (res?.status !== 200 || data.error) {
        const message =
          data.message ||
          (data.errors && Object.values(data.errors).flat().join(" ")) ||
          "We couldn't start your subscription. Please try again.";
        alert(message);
        resetCheckout();
        return;
      }

      // The backend now stores the subscription row on create and returns its
      // status, so the common case needs no polling at all.
      if (data.status === "active" || data.status === "trialing") {
        await finishActivation();
        return;
      }

      // Fallback: wait a bounded time for the webhook to activate it.
      const maxAttempts = 20;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const result = await Api.get("/web/user-expiration");
        if (result?.status === 401) {
          alert("Your session has expired. Please sign in again.");
          window.location.href = signinRedirect;
          return;
        }
        if (result?.data?.active) {
          await finishActivation();
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      alert(
        "Your payment is being processed. Your access will activate shortly — please check your profile in a minute."
      );
      resetCheckout();
      navigate(successPath);
    } catch (error) {
      console.error("Error activating subscription:", error);
      alert(
        "Something went wrong while activating your subscription. Please check your profile or try again."
      );
      resetCheckout();
    }
  };

  const closeCardModal = () => {
    // Clearing the secret brings the Enroll button back;
    // a fresh SetupIntent is created on the next attempt.
    setShowModal(false);
    setClientSecret(null);
  };

  return {
    isLoggedIn,
    user,
    program,
    programLoading,
    selectedPlan,
    setSelectedPlan,
    selectedGateway,
    setSelectedGateway,
    loading,
    clientSecret,
    showModal,
    activating,
    hasActiveAccess,
    enrollNow,
    onCardConfirmed,
    closeCardModal,
  };
};

export default useSubscriptionCheckout;
